import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LONG_PRESS_DURATION = 750; // ms
const PULSE_DURATION = 120; // ms

interface StartHuntButtonProps {
  navigateUrl: string;
}

export function StartHuntButton({ navigateUrl }: StartHuntButtonProps) {
  const navigate = useNavigate();
  const [isPressing, setIsPressing] = useState(false);
  const [fillPct, setFillPct] = useState(0);
  const [isPulsing, setIsPulsing] = useState(false);

  const rafRef = useRef<number | null>(null);
  const pressStartRef = useRef<number | null>(null);
  const completedRef = useRef(false);

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const cancelPress = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pressStartRef.current = null;
    setIsPressing(false);
    // Animate fill back to 0 in ~150ms
    setFillPct(0);
    completedRef.current = false;
  }, []);

  const handlePointerDown = useCallback(() => {
    if (prefersReducedMotion) return;

    completedRef.current = false;
    pressStartRef.current = performance.now();
    setIsPressing(true);
    setFillPct(0);

    function tick() {
      const start = pressStartRef.current;
      if (start === null) return;

      const elapsed = performance.now() - start;
      const pct = Math.min(100, (elapsed / LONG_PRESS_DURATION) * 100);
      setFillPct(pct);

      if (elapsed >= LONG_PRESS_DURATION) {
        // Long press complete
        completedRef.current = true;
        rafRef.current = null;
        pressStartRef.current = null;
        setIsPressing(false);
        setFillPct(100);
        setIsPulsing(true);
        // Vibrate on supported devices
        navigator.vibrate?.(50);
        // Brief pulse then navigate
        setTimeout(() => {
          setIsPulsing(false);
          navigate(navigateUrl);
        }, PULSE_DURATION);
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
  }, [prefersReducedMotion, navigate, navigateUrl]);

  const handlePointerUp = useCallback(() => {
    if (prefersReducedMotion) return;
    if (!completedRef.current) {
      cancelPress();
    }
  }, [prefersReducedMotion, cancelPress]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  if (prefersReducedMotion) {
    return (
      <button
        type="button"
        onClick={() => navigate(navigateUrl)}
        className="relative w-full px-5 py-5 bg-ember-500 rounded-lg shadow-ember border-t-[0.5px] border-ember-400/20 flex items-center justify-center gap-3 mt-3 overflow-hidden"
        data-testid="start-hunt-button"
        data-variant="tactical-switch"
        aria-label="Start a hunt"
      >
        <span className="font-sans font-bold text-[14px] tracking-widest uppercase text-white relative z-10">
          START HUNT
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="relative z-10"
        >
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    );
  }

  return (
    <button
      type="button"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      className={[
        'relative w-full px-5 py-5 bg-ember-500 rounded-lg shadow-ember border-t-[0.5px] border-ember-400/20 flex items-center justify-center gap-3 mt-3 overflow-hidden select-none',
        isPulsing ? 'scale-[1.02]' : '',
      ].join(' ')}
      data-testid="start-hunt-button"
      data-variant="tactical-switch"
      data-pressing={isPressing ? 'true' : 'false'}
      data-fill-pct={Math.round(fillPct)}
      aria-label="Start a hunt"
      style={{ transition: isPulsing ? `transform ${PULSE_DURATION}ms ease-out` : undefined }}
    >
      {/* Fill overlay — rAF-driven width, linear */}
      <div
        aria-hidden
        className="absolute inset-0 bg-white/10 origin-left pointer-events-none"
        style={{ width: `${fillPct}%`, transition: fillPct === 0 ? 'width 150ms linear' : 'none' }}
      />
      <span className="font-sans font-bold text-[14px] tracking-widest uppercase text-white relative z-10">
        START HUNT
      </span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="relative z-10"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}
